import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from '@/api/business'
import { createUser } from '@/api/business/user'
import { UserKeys } from '@/queries/useUserQuery'
import Button from '@/components/Button'
import { Input } from '@/components/ui/input' 
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    
} from '@/components/ui/form'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from "@/components/ui/select"
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod' 
import { useBaseStore } from '@/store'
import { authStateSelector } from '@/store/authSlice'

interface AddDialogProps {
    isOpen: boolean
    onClose: () => void
}

const AddDialog = ({ isOpen, onClose }: AddDialogProps) => {
    const queryClient = useQueryClient()
    const { toast } = useToast() 
    const { profile } = useBaseStore(authStateSelector)

    const schema = z.object({
        NM_USUARIO: z.string().min(1, {
            message: 'O campo Nome do Usuário é obrigatório',
        }),
        DD_EMAIL: z.string().min(1, {
            message: 'O campo E-mail é obrigatório',
        }),
        DD_NUMERO_CONTATO:z.string(),
        DS_USUARIO: z.string().min(1, {
            message: 'O campo Usuário de login é obrigatório',
        }),
        DS_SENHA: z.string()
            .min(8, { message: "A senha deve ter pelo menos 8 caracteres" })
            .regex(/[A-Z]/, { message: "A senha deve conter pelo menos uma letra maiúscula" })
            .regex(/[a-z]/, { message: "A senha deve conter pelo menos uma letra minúscula" })
            .regex(/[0-9]/, { message: "A senha deve conter pelo menos um número" })
            .regex(/[\W_]/, { message: "A senha deve conter pelo menos um caractere especial" }),
        CONFIRMAR_SENHA: z.string().min(1, {
            message: 'O campo Confirmar Senha é obrigatório',
        }),
        DD_STATUS: z.union([z.literal(0), z.literal(1)]),
        DD_PERFIL: z.union([z.literal("NOVO"), z.literal("ADMINISTRADOR"), z.literal("REPRESENTANTE"), z.literal("ADMINISTRADOR_REVENDA")]),
    }).refine((data) => data.DS_SENHA === data.CONFIRMAR_SENHA, {
        path: ["CONFIRMAR_SENHA"], 
        message: "As senhas não conferem",
    });

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            NM_USUARIO: '',
            DD_EMAIL: '',
            DD_NUMERO_CONTATO: '',
            DS_USUARIO: '',
            DS_SENHA: '',
            CONFIRMAR_SENHA: '',
            DD_STATUS: 1,
            DD_PERFIL: profile === 'ADMINISTRADOR_REVENDA' ? 'REPRESENTANTE' : 'NOVO'
        },
    })

    const { mutate, isPending: mutateLoading } = useMutation({
        mutationFn: createUser,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: UserKeys.lists() })
            onClose()
            form.reset()
            toast({
                title: 'Usuário criado com sucesso',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível criar o usuário. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao criar usuário',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSubmit = (data: z.infer<typeof schema>) => {
        mutate({
            ...data
        })
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[700px] overflow-auto'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Adicionar Usuário</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    >
                        <div className='grid grid-cols-[2fr,1fr] gap-4'>
                            <FormField
                                name="NM_USUARIO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Nome do usuário</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                name="DD_NUMERO_CONTATO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Número de Contato</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div>
                        <div className='grid gap-4'>
                            <FormField
                                name="DD_EMAIL"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>E-mail</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div>
                         <div className='grid grid-cols-[2fr,1fr] gap-4'>
                            <FormField
                                name="DS_USUARIO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Login</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                name="DD_PERFIL"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Perfil</FormLabel>
                                         <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value.toString()} disabled={profile === 'ADMINISTRADOR_REVENDA'}>
                                            <FormControl>
                                                <SelectTrigger>
                                                <SelectValue placeholder="" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value='NOVO'>Novo</SelectItem>
                                                <SelectItem value="ADMINISTRADOR">Administrador</SelectItem>
                                                <SelectItem value="REPRESENTANTE">Representante</SelectItem>
                                                <SelectItem value="ADMINISTRADOR_REVENDA">Administrador da Revenda</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div>
                        <div className='grid grid-cols-[1fr,1fr] gap-4'>
                            <FormField
                                name="DS_SENHA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Senha de acesso</FormLabel>
                                        <FormControl>
                                            <Input {...field} type='password'/>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                name="CONFIRMAR_SENHA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Confirme a Senha</FormLabel>
                                        <FormControl>
                                            <Input {...field} type='password' />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />   
                        
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={mutateLoading}>
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default AddDialog
